import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { DollarSign, CalendarCheck, CheckCircle2, XCircle, Clock, TrendingUp } from "lucide-react";

function Kpi({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-zinc-400">{label}</span>
        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${accent}`}><Icon className="w-4 h-4" /></div>
      </div>
      <div className="text-2xl font-bold text-white mt-3">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)); }, []);
  if (!data) return <p className="text-zinc-400">Carregando...</p>;

  return (
    <div>
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-1">Dashboard</h1>
      <p className="text-zinc-400 mb-6">Visão geral do dia na Barbearia Big Black.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Kpi icon={DollarSign} label="Faturamento Hoje" value={money(data.revenue_today)} accent="bg-green-500/15 text-green-400" testid="kpi-revenue-today" />
        <Kpi icon={TrendingUp} label="Faturamento Mês" value={money(data.revenue_month)} accent="bg-amber-500/15 text-amber-400" testid="kpi-revenue-month" />
        <Kpi icon={CalendarCheck} label="Agendamentos Hoje" value={data.count_today} accent="bg-blue-500/15 text-blue-400" testid="kpi-count-today" />
        <Kpi icon={CheckCircle2} label="Concluídos Hoje" value={data.completed_today} accent="bg-emerald-500/15 text-emerald-400" testid="kpi-completed-today" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
          <h2 className="font-heading text-lg font-bold text-white mb-4 flex items-center gap-2"><Clock className="w-5 h-5 text-amber-400" /> Agenda de Hoje</h2>
          {data.today_appointments.length === 0 ? (
            <p className="text-zinc-500 py-8 text-center">Nenhum agendamento para hoje.</p>
          ) : (
            <div className="space-y-3">
              {data.today_appointments.map((a) => (
                <div key={a.id} className="flex items-center gap-4 p-3 rounded-xl bg-zinc-800/50" data-testid={`dash-appt-${a.id}`}>
                  <div className="text-center w-16 shrink-0">
                    <div className="text-amber-400 font-bold">{a.start_time}</div>
                    <div className="text-xs text-zinc-500">{a.end_time}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-semibold truncate">{a.client_name}</div>
                    <div className="text-sm text-zinc-400 truncate">{a.service_name} • {a.barber_name}</div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${a.status === "completed" ? "bg-green-500/15 text-green-400" : a.status === "cancelled" ? "bg-red-500/15 text-red-400" : "bg-blue-500/15 text-blue-400"}`}>
                    {a.status === "completed" ? "Concluído" : a.status === "cancelled" ? "Cancelado" : "Confirmado"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
            <h3 className="text-sm text-zinc-400 mb-3">Resumo do dia</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between"><span className="text-zinc-400 flex items-center gap-2"><CalendarCheck className="w-4 h-4 text-blue-400" /> Total</span><span className="text-white font-bold">{data.count_today}</span></div>
              <div className="flex items-center justify-between"><span className="text-zinc-400 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-400" /> Concluídos</span><span className="text-white font-bold">{data.completed_today}</span></div>
              <div className="flex items-center justify-between"><span className="text-zinc-400 flex items-center gap-2"><XCircle className="w-4 h-4 text-red-400" /> Cancelados</span><span className="text-white font-bold">{data.cancelled_today}</span></div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-amber-500/20 to-zinc-900 border border-amber-500/30 rounded-2xl p-6">
            <h3 className="text-sm text-amber-300 mb-1">Próximos clientes</h3>
            {data.upcoming.length === 0 ? <p className="text-zinc-500 text-sm mt-2">Sem próximos clientes.</p> : (
              <div className="space-y-2 mt-3">
                {data.upcoming.slice(0, 5).map((a) => (
                  <div key={a.id} className="flex justify-between text-sm"><span className="text-white truncate">{a.client_name}</span><span className="text-amber-400 font-semibold">{a.start_time}</span></div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
